import React, { useEffect, useRef, useCallback, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import type { Video } from '@/models';
import { APIRoutes } from '@/services/api.routes';
import { useUser } from '@/contexts/UserContext';
import type { UpdateVideoProgressRequest } from '@/modelsAPI';

interface BunnyVideoPlayerProps {
  video: Video;
  className?: string;
  onProgressUpdate?: (progress: number) => void;
}

const BunnyVideoPlayer: React.FC<BunnyVideoPlayerProps> = ({
  video,
  className = '',
  onProgressUpdate
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const playerRef = useRef<PlayerJsPlayer | null>(null);
  const { user } = useUser();
  const [lastProgressUpdate, setLastProgressUpdate] = useState<number>(0);
  const progressUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Debounced progress update function
  const updateProgress = useCallback(async (progress: number) => {
    // Only update if progress has changed significantly (at least 5%)
    // Exception: always update when video is completed (progress >= 1.0)
    if (progress < 1.0 && Math.abs(progress - lastProgressUpdate) < 0.05) {
      return;
    }

    // Clear existing timeout
    if (progressUpdateTimeoutRef.current) {
      clearTimeout(progressUpdateTimeoutRef.current);
    }

    // For video completion (progress >= 1.0), update immediately without debounce
    if (progress >= 1.0) {
      try {
        if (user?.id && video.id) {
          const request: UpdateVideoProgressRequest = {
            progress: 1.0 // Ensure it's exactly 1.0 for completion
          };

          await APIRoutes.updateVideoProgress(video.id, request);
          setLastProgressUpdate(progress);

          // Call optional callback AFTER successful save
          onProgressUpdate?.(progress);
        }
      } catch (error) {
        console.error('Failed to update video progress:', error);
      }
    } else {
      // Set new timeout for debounced update (for regular progress updates)
      progressUpdateTimeoutRef.current = setTimeout(async () => {
        try {
          if (user?.id && video.id) {
            const request: UpdateVideoProgressRequest = {
              progress: Math.min(Math.max(progress, 0), 1) // Clamp between 0 and 1
            };

            await APIRoutes.updateVideoProgress(video.id, request);
            setLastProgressUpdate(progress);

            // Call optional callback AFTER successful save
            onProgressUpdate?.(progress);
          }
        } catch (error) {
          console.error('Failed to update video progress:', error);
        }
      }, 2000); // 2 second debounce
    }
  }, [user?.id, video.id, lastProgressUpdate, onProgressUpdate]);

  // Load PlayerJS script
  useEffect(() => {
    const loadPlayerJS = () => {
      if (window.playerjs) {
        return;
      }

      const script = document.createElement('script');
      script.src = '//assets.mediadelivery.net/playerjs/player-0.1.0.min.js';
      script.async = true;
      script.onerror = () => {
        console.error('Failed to load PlayerJS');
      };
      document.head.appendChild(script);
    };

    loadPlayerJS();
  }, []);

  // Initialize PlayerJS when iframe is ready
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || !video.bunnyURL) return;

    const initializePlayer = () => {
      // Check if PlayerJS is available
      if (!window.playerjs) {
        console.warn('PlayerJS not available, using fallback method');
        setupFallbackProgressTracking();
        return;
      }

      try {
        // Clean up existing player
        if (playerRef.current && typeof playerRef.current.destroy === 'function') {
          try {
            playerRef.current.destroy();
          } catch (error) {
            console.debug('Error destroying existing player:', error);
          }
          playerRef.current = null;
        }

        // Create new player instance
        const player = new window.playerjs.Player(iframe);

        // Verify player was created successfully
        if (!player || typeof player.on !== 'function') {
          throw new Error('PlayerJS player creation failed');
        }

        playerRef.current = player;

        // Set up event listeners
        player.on('timeupdate', (data: unknown) => {
          try {
            const timingData = typeof data === 'string' ? JSON.parse(data) : data;
            const parsedData = timingData as { seconds?: number; duration?: number };
            const currentTime = parsedData.seconds || 0;
            const duration = parsedData.duration || 1;
            const progress = duration > 0 ? currentTime / duration : 0;

            updateProgress(progress);
          } catch (error) {
            console.debug('Error parsing timeupdate data:', error);
          }
        });

        player.on('ended', () => {
          updateProgress(1.0);
        });

      } catch (error) {
        console.error('Failed to initialize PlayerJS:', error);
        // Clean up any partial player instance
        if (playerRef.current) {
          playerRef.current = null;
        }
        // Fallback to alternative method
        setupFallbackProgressTracking();
      }
    };

    // Fallback method using postMessage communication
    const setupFallbackProgressTracking = () => {
      const handleMessage = (event: MessageEvent) => {
        // Only process messages from our iframe
        if (event.source !== iframe.contentWindow) {
          return;
        }

        try {
          const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

          // Handle Bunny player events
          if (data.type === 'bunny-player-progress') {
            const progress = data.progress || 0;
            updateProgress(progress);
          } else if (data.type === 'bunny-player-timeupdate') {
            // Calculate progress from current time and duration
            const currentTime = data.currentTime || 0;
            const duration = data.duration || 1;
            const progress = duration > 0 ? currentTime / duration : 0;
            updateProgress(progress);
          }
        } catch (error) {
          // Ignore invalid JSON or other errors
          console.debug('Ignored message from iframe:', error);
        }
      };

      window.addEventListener('message', handleMessage);

      // Try to inject progress tracking script
      try {
        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
        if (iframeDoc) {
          const script = iframeDoc.createElement('script');
          script.textContent = `
            (function() {
              // Wait for video element to be available
              function waitForVideo() {
                const video = document.querySelector('video');
                if (video) {
                  let lastProgress = 0;
                  
                  // Track time updates
                  video.addEventListener('timeupdate', function() {
                    const progress = video.duration > 0 ? video.currentTime / video.duration : 0;
                    
                    // Only send significant progress updates
                    if (Math.abs(progress - lastProgress) >= 0.05) {
                      lastProgress = progress;
                      window.parent.postMessage({
                        type: 'bunny-player-timeupdate',
                        currentTime: video.currentTime,
                        duration: video.duration,
                        progress: progress
                      }, '*');
                    }
                  });
                  
                  // Track when video ends
                  video.addEventListener('ended', function() {
                    window.parent.postMessage({
                      type: 'bunny-player-progress',
                      progress: 1.0
                    }, '*');
                  });
                  
                  // Track when video starts playing
                  video.addEventListener('play', function() {
                    window.parent.postMessage({
                      type: 'bunny-player-started',
                      progress: 0
                    }, '*');
                  });
                } else {
                  // Retry after a short delay
                  setTimeout(waitForVideo, 500);
                }
              }
              
              waitForVideo();
            })();
          `;
          iframeDoc.head.appendChild(script);
        }
      } catch (error) {
        // Cross-origin restrictions - this is expected for external iframes
        console.debug('Cannot inject script into iframe due to CORS:', error);
      }

      // Return cleanup function
      return () => {
        window.removeEventListener('message', handleMessage);
      };
    };

    const handleIframeLoad = () => {
      // Small delay to ensure iframe is fully loaded
      setTimeout(initializePlayer, 500);
    };

    iframe.addEventListener('load', handleIframeLoad);

    // If iframe is already loaded, initialize immediately
    if (iframe.contentDocument?.readyState === 'complete') {
      handleIframeLoad();
    }

    return () => {
      iframe.removeEventListener('load', handleIframeLoad);
      if (playerRef.current && typeof playerRef.current.destroy === 'function') {
        try {
          playerRef.current.destroy();
        } catch (error) {
          console.debug('Error destroying player:', error);
        }
        playerRef.current = null;
      }
    };
  }, [video.bunnyURL, updateProgress]);

  if (!video.bunnyURL) {
    return (
      <div className={`relative bg-black rounded-lg flex items-center justify-center aspect-video ${className}`}>
        <div className="text-center text-white">
          <FontAwesomeIcon icon={faExclamationTriangle} className="w-8 h-8 mb-4" />
          <p>Video nicht verfügbar</p>
          <p className="text-sm opacity-75">Bunny URL fehlt</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden ${className}`} style={{ paddingTop: '56.25%' }}>
      <iframe
        ref={iframeRef}
        src={video.bunnyURL}
        loading="lazy"
        style={{
          border: 0,
          position: 'absolute',
          top: 0,
          height: '100%',
          width: '100%'
        }}
        allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;"
        allowFullScreen={true}
        title={video.title}
      />
    </div>
  );
};

export default BunnyVideoPlayer;
